import type { Student } from "../providers/studentsProvider";
import { FormProvider, useForm } from "react-hook-form";
import { Alert, Box, Dialog, DialogActions, DialogContent, DialogTitle } from "@mui/material";
import { NumberInput } from "react-admin";
import type { DialogProps } from "../misc/DialogProps";
import { DialogCloseButton } from "../misc/DialogCloseButton";
import LoadingButton from "@mui/lab/LoadingButton";
import { DateInput } from "../inputs/DateInput";
import { Form2Theme } from "../misc/Form2";
import { Column } from "../misc/Column";
import { WhenFormIsInitialized } from "../misc/WhenFormIsInitialized";
import { LoadingIndicator } from "../misc/LoadingIndicator";
import { getAuthenticatedServerClient, runSafelyWithServerClient } from "../api/server.api";
import { DateTime } from "luxon";
import { useState } from "react";
import { createPdfPreview, PdfPreviewDialogContent, type PdfPreview } from "./PdfPreviewDialogContent";

export function CreateSchaltnachweisDialog(props: { student: Student } & DialogProps) {
  const { student, ...dialogProps } = props;
  const [preview, setPreview] = useState<undefined | PdfPreview>();
  const closeDialog = () => {
    setPreview(undefined);
    dialogProps.onClose();
  };

  return (
    <Dialog {...dialogProps} onClose={closeDialog} maxWidth={!preview ? "sm" : "lg"} fullWidth>
      <DialogTitle>{!preview ? "Schaltnachweis erstellen" : "Vorschau"}</DialogTitle>
      <DialogCloseButton onClick={closeDialog} />
      {!preview ? (
        <_FormDialogContentAndActions student={student} setPreview={setPreview} />
      ) : (
        <PdfPreviewDialogContent
          student={student}
          preview={preview}
          documentName="Schaltnachweis"
          documentNameWithArticle="des Schaltnachweises"
          signatureRequestType="schaltnachweis"
          closeDialog={closeDialog}
        />
      )}
    </Dialog>
  );
}

interface _FormValues {
  eductionFrom: DateTime | null;
  eductionTo: DateTime | null;
  numberOf45MinUnits: number;
  dateOfTestfahrt: DateTime | null;
}

function _FormDialogContentAndActions(props: { student: Student; setPreview: (preview: PdfPreview) => void }) {
  const { student, setPreview } = props;
  const form = useForm<_FormValues>({
    defaultValues: async () => {
      const serverClient = await getAuthenticatedServerClient();
      const dto = await serverClient.getSchaltnachweisData({ params: { studentId: student.id } });
      const { eductionFrom, eductionTo, numberOf45MinUnits, dateOfTestfahrt } = dto;
      return {
        eductionFrom: eductionFrom ? DateTime.fromISO(eductionFrom) : null,
        eductionTo: eductionTo ? DateTime.fromISO(eductionTo) : null,
        numberOf45MinUnits,
        dateOfTestfahrt: dateOfTestfahrt ? DateTime.fromISO(dateOfTestfahrt) : null,
      };
    },
  });
  const [creatingPreview, setCreatingPreview] = useState(false);

  const createPreview = async (formData: _FormValues) => {
    setCreatingPreview(true);
    await runSafelyWithServerClient(
      async (serverClient) => {
        const preview = await createPdfPreview({
          serverClient,
          createPdf: () =>
            serverClient.createSchaltnachweis({
              params: { studentId: student.id },
              queries: {
                data: JSON.stringify({
                  eductionFrom: formData.eductionFrom!.toISODate(),
                  eductionTo: formData.eductionTo!.toISODate(),
                  numberOf45MinUnits: formData.numberOf45MinUnits,
                  dateOfTestfahrt: formData.dateOfTestfahrt!.toISODate(),
                }),
              },
            }),
        });
        setPreview(preview);
      },
      {
        errorMessage: "Fehler beim Erstellen der Vorschau.",
        successMessage: "",
      },
    );
    setCreatingPreview(false);
  };

  return (
    <Form2Theme>
      <FormProvider {...form}>
        <form>
          <DialogContent>
            <Column>
              <Alert severity="info" sx={{ mb: 3 }}>
                {`Du kannst hier den Schaltnachweis für ${student.callName} ohne Unter\u00ADschrift erstellen. Er wird automatisch der Unter\u00ADschriften\u00ADmappe des Fahrschul\u00ADleiters hinzugefügt.`}
              </Alert>
              <WhenFormIsInitialized
                placeholder={
                  <Box sx={{ position: "relative", height: "345px" }}>
                    <LoadingIndicator />
                  </Box>
                }
              >
                <DateInput
                  source="eductionFrom"
                  label="Datum Ausbildungsbeginn"
                  allowPastDates
                  size="small"
                  helperText=" " // <-- always add some space below the input field
                />
                <DateInput
                  source="eductionTo"
                  label="Datum Ausbildungsende"
                  allowPastDates
                  size="small"
                  helperText=" " // <-- always add some space below the input field
                />
                <NumberInput
                  source="numberOf45MinUnits"
                  label="Anzahl Schalterstunden"
                  size="small"
                  required
                  validate={(value) =>
                    value < 10
                      ? "Es sind mindestens 10 Schalterstunden für einen gültigen Schaltnachweis erforderlich."
                      : undefined
                  }
                />
                <DateInput
                  source="dateOfTestfahrt"
                  label="Datum Testfahrt Schaltkompetenz"
                  allowPastDates
                  size="small"
                  helperText=" " // <-- always add some space below the input field
                />
              </WhenFormIsInitialized>{" "}
            </Column>
          </DialogContent>
          <DialogActions>
            <LoadingButton variant="contained" loading={creatingPreview} onClick={form.handleSubmit(createPreview)}>
              Vorschau
            </LoadingButton>
          </DialogActions>
        </form>
      </FormProvider>
    </Form2Theme>
  );
}
